import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
 
/**
 * Build the data object for the template.
 *
 * @param {string} courseName  - e.g. "BSB50420 Diploma of Leadership and Management"
 * @param {Array}  studentList - [{ studentId, studentName }, ...]
 * @returns {{ UNIT_CODE: string, students: Array }}
 */
export function createAttendanceMapping({
  courseName,
  studentList,
  attendanceDetails = {},
}) {
  return {
    UNIT_CODE: attendanceDetails.UNIT_CODE || courseName || "",
    a_g: attendanceDetails.a_g || "",
    a_unit_name: attendanceDetails.a_unit_name || courseName || "",
    a_w: attendanceDetails.a_w || "",
    a_trainer: attendanceDetails.a_trainer || "",
    w_s: attendanceDetails.w_s || "",
    w_e: attendanceDetails.w_e || "",
    a_r: attendanceDetails.a_r || "",
    a_b: attendanceDetails.a_b || "",
    a_t: attendanceDetails.a_t || "",
    a_y: attendanceDetails.a_y || "",
    a_d1: attendanceDetails.a_d1 || "",
    a_d2: attendanceDetails.a_d2 || "",
    a_d3: attendanceDetails.a_d3 || "",
    a_d4: attendanceDetails.a_d4 || "",
    a_d5: attendanceDetails.a_d5 || "",
    totalHours: attendanceDetails.totalHours || "20hr / 1hr",

    students: studentList.map((s, idx) => ({
      i: String(idx + 1),
      studentId: s.studentId || "",
      studentName: s.studentName || "",
    })),

  };
}

 
/**
 * Fetch the template file from the public folder.
 *
 * @param {string} templatePath  - e.g. "/templates/attendance_template.docx"
 * @returns {Promise<ArrayBuffer>}
 */
async function fetchTemplate(templatePath) {
  const res = await fetch(templatePath);
  if (!res.ok) throw new Error(`Failed to load template: ${templatePath} (${res.status})`);
  return res.arrayBuffer();
}
 
/**
 * Fill one course sheet and return a Blob.
 *
 * @param {ArrayBuffer} templateBuffer  - raw bytes of the .docx template
 * @param {object}      data            - output of createAttendanceMapping()
 * @returns {Blob}
 */
function fillTemplate(templateBuffer, data) {
  const zip = new PizZip(templateBuffer);
 
  const doc = new Docxtemplater(zip, {
    // Use [[ ]] delimiters to match your template
    delimiters: { start: "[[", end: "]]" },
    paragraphLoop: false,   // clean loop — removes the tag paragraphs themselves
    linebreaks:   true,
  });
 
  doc.render(data);
 
  return doc.getZip().generate({
    type:        "blob",
    mimeType:    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    compression: "DEFLATE",
  });
}
 
/**
 * Generate and download a single course attendance sheet.
 *
 * @param {object} options
 * @param {string} options.templatePath  - public path to .docx template
 * @param {string} options.courseName
 * @param {Array}  options.studentList   - [{ studentId, studentName }]
 * @param {string} options.filename      - output filename (without .docx)
 */
export async function downloadCourseSheet({
  templatePath,
  courseName,
  studentList,
  filename,
  attendanceDetails = {},
}) {
  const templateBuffer = await fetchTemplate(templatePath);

  const data = createAttendanceMapping({
    courseName,
    studentList,
    attendanceDetails,
  });

  const blob = fillTemplate(templateBuffer, data);
  saveAs(blob, `${filename}.docx`);
}
 
/**
 * Generate and download ALL courses for a campus as separate sheets
 * bundled into a single ZIP (requires jszip).
 *
 * @param {object} options
 * @param {string} options.templatePath
 * @param {string} options.campus             - "Canberra" | "Melbourne"
 * @param {object} options.courseMap          - { [courseName]: studentList[] }
 * @param {string} options.weekTag            - e.g. "07-04-2026"
 */
export async function downloadCampusZip({ templatePath, campus, courseMap, weekTag }) {
  const JSZip          = (await import("jszip")).default;
  const templateBuffer = await fetchTemplate(templatePath);
  const archive        = new JSZip();

  for (const [courseName, studentList] of Object.entries(courseMap)) {
    const data = createAttendanceMapping({ courseName, studentList, attendanceDetails: {}, });
    const blob = fillTemplate(templateBuffer, data);
    // courseName here is already the display name e.g. "CPC30220 Certificate III in Carpentry (COE 3days)"
    const safeName = courseName.slice(0, 50).replace(/[/\\:*?"<>|]/g, "_");
    archive.file(`${safeName}.docx`, blob);
  }

  const zipBlob = await archive.generateAsync({ type: "blob", compression: "DEFLATE" });
  // ZIP filename: attendance_Canberra_3days_07-04-2026.zip  (campus already has suffix from templateLabels)
  saveAs(zipBlob, `attendance_${campus}_${weekTag}.zip`);
}